import type { Brand } from "../types";

import { Button, Chip } from "@heroui/react";
import { Pencil, Plus } from "lucide-react";
import { useMemo, useState } from "react";

import { AddBrandModal } from "@/components/brands/add-brand";
import { EditBrandModal } from "@/components/brands/edit-brand";
import {
  DataTable,
  ImagePreviewModal,
  ImageTile,
  SearchBox,
  useClientTable,
  type DataGridColumn,
  type PreviewImage,
} from "@/components/kit";
import { useGetAllBrands } from "@/hooks/api/brand/useGetAllBrands";
import { useOpenOnAddParam } from "@/hooks/useOpenOnAddParam";
import { AppPage } from "@/layouts/page";

export default function BrandsPage() {
  const { data: brands, isPending, refetch } = useGetAllBrands();

  const [isAddOpen, setIsAddOpen] = useState(false);
  const [editingBrand, setEditingBrand] = useState<Brand | null>(null);
  const [preview, setPreview] = useState<PreviewImage | null>(null);
  const [searchTerm, setSearchTerm] = useState("");

  useOpenOnAddParam(() => setIsAddOpen(true));

  const filteredBrands = useMemo(() => {
    if (!brands) return [];
    const term = searchTerm.toLowerCase();

    if (!term) return brands;

    return brands.filter(
      (brand) =>
        brand.name.toLowerCase().includes(term) ||
        brand.nameAr?.toLowerCase().includes(term) ||
        brand.id.toLowerCase().includes(term),
    );
  }, [brands, searchTerm]);

  const columns = useMemo<DataGridColumn<Brand>[]>(
    () => [
      {
        id: "name",
        header: "Brand",
        isRowHeader: true,
        allowsSorting: true,
        sortFn: (a, b) => a.name.localeCompare(b.name),
        cell: (brand) => (
          <div className="flex items-center gap-3">
            <button
              aria-label={brand.name}
              className="cursor-(--cursor-interactive) rounded-xl"
              type="button"
              onClick={() => setPreview({ url: brand.image, name: brand.name })}
            >
              <ImageTile src={brand.image} />
            </button>
            <div className="flex min-w-0 flex-col leading-tight">
              <span className="truncate font-medium">{brand.name}</span>
              <span
                className="text-muted truncate text-left text-xs"
                lang={brand.nameAr ? "ar" : undefined}
              >
                {brand.nameAr || "No Arabic name"}
              </span>
            </div>
          </div>
        ),
      },
      {
        id: "isLocal",
        header: "Origin",
        width: 160,
        allowsSorting: true,
        sortFn: (a, b) => Number(b.isLocal) - Number(a.isLocal),
        cell: (brand) => (
          <Chip color={brand.isLocal ? "accent" : "default"} variant="soft">
            {brand.isLocal ? "Local" : "International"}
          </Chip>
        ),
      },
      {
        id: "isActive",
        header: "Status",
        width: 140,
        allowsSorting: true,
        sortFn: (a, b) => Number(b.isActive) - Number(a.isActive),
        cell: (brand) => (
          <Chip color={brand.isActive ? "success" : "default"} variant="soft">
            {brand.isActive ? "Active" : "Inactive"}
          </Chip>
        ),
      },
      {
        id: "id",
        header: "ID",
        width: 180,
        cell: (brand) => (
          <span className="text-muted block max-w-40 truncate font-mono text-xs">
            {brand.id}
          </span>
        ),
      },
      {
        id: "actions",
        header: () => <span className="sr-only">Actions</span>,
        width: 64,
        align: "end",
        cell: (brand) => (
          <Button
            isIconOnly
            aria-label={`Edit ${brand.name}`}
            size="sm"
            variant="ghost"
            onPress={() => setEditingBrand(brand)}
          >
            <Pencil className="size-4" />
          </Button>
        ),
      },
    ],
    [],
  );

  const table = useClientTable(filteredBrands, {
    columns,
    initialSort: { column: "name", direction: "ascending" },
  });

  return (
    <AppPage
      action={
        <Button onPress={() => setIsAddOpen(true)}>
          <Plus className="size-4" />
          Add brand
        </Button>
      }
      subtitle={
        brands ? (
          <>
            <span className="tabular-nums">{brands.length}</span>{" "}
            {brands.length === 1 ? "brand" : "brands"} in the catalog
          </>
        ) : (
          "Manage product brands and their details"
        )
      }
      title="Brands"
    >
      <div className="mb-4 flex shrink-0">
        <SearchBox
          placeholder="Search brands"
          value={searchTerm}
          onChange={setSearchTerm}
        />
      </div>

      <DataTable
        compact
        columns={columns}
        empty={{
          title: searchTerm ? "No brands match your search" : "No brands yet",
          description: searchTerm
            ? "Try a different name or ID."
            : "Add the first brand to get started.",
          action: searchTerm ? (
            <Button
              size="sm"
              variant="secondary"
              onPress={() => setSearchTerm("")}
            >
              Clear search
            </Button>
          ) : undefined,
        }}
        getRowId={(brand) => brand.id}
        isLoading={isPending}
        label="Brands"
        pagination={table.pagination}
        rows={table.rows}
        sort={table.sort}
        onSortChange={table.onSortChange}
      />

      <ImagePreviewModal
        image={preview}
        title="Brand Image"
        onClose={() => setPreview(null)}
      />

      <AddBrandModal
        isOpen={isAddOpen}
        onClose={() => setIsAddOpen(false)}
        onSuccess={() => {
          refetch();
          setIsAddOpen(false);
        }}
      />

      <EditBrandModal
        brand={editingBrand}
        isOpen={!!editingBrand}
        onClose={() => setEditingBrand(null)}
        onSuccess={() => {
          refetch();
          setEditingBrand(null);
        }}
      />
    </AppPage>
  );
}
